import { useState, useEffect } from 'react'
import { UseFormWatch } from 'react-hook-form'

interface ConditionalField<T = any> {
    field: string
    dependsOn: string
    condition: boolean | string | string[] | ((value: any, formData: T) => boolean)
}

interface useFormProgressProps<T = any> {
    watch: UseFormWatch<any>
    excludedFields?: string[]
    conditionalFields?: ConditionalField<T>[]
}

export const useFormProgress = <T extends Record<string, any>>({
    watch,
    excludedFields = [],
    conditionalFields = []
}: useFormProgressProps<T>): number => {
    const [progress, setProgress] = useState(0)

    const validateCondition = (
        dependentValue: any,
        condition: boolean | string | string[] | ((value: any, formData: T) => boolean),
        formData: Partial<T>
    ): boolean => {
        if (typeof condition === 'function') {
            return condition(dependentValue, formData as T)
        }
        if (typeof condition === 'boolean') {
            return dependentValue === condition
        }
        if (Array.isArray(condition)) {
            return condition.includes(dependentValue)
        }

        return dependentValue === condition
    }

    const getValidFields = (formData: Partial<T>): string[] => {
        if (!formData) {
            return []
        }


        // Start with fields that exist in formData
        const existingFields = Object.keys(formData).filter((field) => {
            const value = formData[field]

            // condition to not count isMinorConfirmed if isMinor is false
            if (field === 'isMinorConfirmed' && formData['isMinor'] === false) {
                return false
            }

            const isExcluded = excludedFields.includes(field)

            return !isExcluded
        })

        // Add conditional fields that should be valid based on their dependencies
        const validConditionalFields = conditionalFields
            .filter((conditionalField) => {
                const dependentValue = formData[conditionalField.dependsOn]

                return validateCondition(
                    dependentValue,
                    conditionalField.condition,
                    formData
                )
            })
            .map((cf) => cf.field)

        // Combine existing fields and valid conditional fields, remove duplicates
        const allValidFields = [
            ...new Set([...existingFields, ...validConditionalFields])
        ]

        // Filter out excluded fields AND conditional fields that don't meet their conditions
        return allValidFields.filter((field) => {
            if (excludedFields.includes(field)) {
                return false
            }

            const conditionalField = conditionalFields.find(
                (cf) => cf.field === field
            )

            // If it's a conditional field, check if its condition is met
            if (conditionalField) {
                const dependentValue = formData[conditionalField.dependsOn]

                return validateCondition(
                    dependentValue,
                    conditionalField.condition,
                    formData
                )
            }

            // If it's not a conditional field, include it
            return true
        })
    }

    const calculateProgress = (formData: Partial<T>): number => {
        const validFields = getValidFields(formData)

        if (validFields.length === 0) {
            return 0
        }

        // Check if array fields have values and handle boolean fields
        const filledFields = validFields.filter((field) => {
            const value = (formData as Record<string, unknown>)[field]

            // Solo para isMinorConfirmed, debe ser true
            if (field === 'isMinorConfirmed') {
                return value === true
            }

            if (typeof value === 'boolean') {
                return value === true // Only count as filled if true
            }

            if (typeof value === 'string') {
                const trimmedValue = value.trim()

                // Special check for phone numbers/cellphones with only country prefix (e.g., "+57")
                if (
                    field.toLowerCase().includes('phone') ||
                    field.toLowerCase().includes('cellphone')
                ) {
                    const digits = trimmedValue.replace(/\D/g, '')

                    // If it has 3 or fewer digits, it's likely just a country code
                    return digits.length > 3
                }

                return trimmedValue.length > 0
            }

            if (Array.isArray(value)) {
                return value.length > 0
            }
            // Handle null/undefined values
            if (value === null || value === undefined) {
                return false
            }

            return !!value
        })




        const percentage = Math.round((filledFields.length / validFields.length) * 100) || 0
        
        return percentage
    }

    useEffect(() => {
        const subscription = watch((formData) => {
            const newProgress = calculateProgress(formData)

            setProgress(newProgress)
        })

        return (): void => subscription.unsubscribe()
    }, [watch, excludedFields, conditionalFields]) // Add conditionalFields as dependency

    return progress
}
